import { useTranslation, type TranslationParams } from '@kgc/i18n';
import { Badge, cn } from '@kgc/ui';
import { AlertTriangle, CheckCircle2, Clock, Loader2, XCircle } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTestHistory, type TestResultHistoryItem } from '../hooks/useTestResults';

interface TestHistoryPopoverProps {
  version: string;
  featureIndex: number;
  historyCount: number;
  totalSteps: number;
}

interface PopoverPosition {
  top: number;
  left: number;
  placement: 'top' | 'bottom';
}

const POPOVER_WIDTH = 360;
const POPOVER_MAX_HEIGHT = 420;
const GAP = 8;

// Format duration between testedAt and archivedAt using i18n keys
function formatDuration(
  fromIso: string,
  toIso: string,
  t: (key: string, vars?: TranslationParams) => string
): string {
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  if (!isFinite(ms) || ms < 0) return '';
  const minutes = Math.round(ms / (1000 * 60));
  if (minutes < 60) return t('releaseHub.testing.durationMinutes', { n: Math.max(1, minutes) });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('releaseHub.testing.durationHours', { n: hours });
  const days = Math.round(hours / 24);
  return t('releaseHub.testing.durationDays', { n: days });
}

// Count checked steps from JSON string stepResults.
// Defensive: rejects non-object JSON and logs (visible via Sentry / DevTools).
function countCheckedSteps(stepResultsJson: string | null): number {
  if (!stepResultsJson) return 0;
  try {
    const parsed = JSON.parse(stepResultsJson) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      console.warn('[release-hub] stepResults JSON was not an object:', stepResultsJson);
      return 0;
    }
    return Object.values(parsed as Record<string, unknown>).filter(v => v === true).length;
  } catch (err) {
    console.warn('[release-hub] failed to parse stepResults JSON:', err);
    return 0;
  }
}

export function TestHistoryPopover({
  version,
  featureIndex,
  historyCount,
  totalSteps,
}: TestHistoryPopoverProps) {
  const { locale, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<PopoverPosition | null>(null);

  const {
    data: history,
    isLoading,
    isError,
    refetch,
  } = useTestHistory(version, featureIndex, isOpen);

  // Compute popover position relative to trigger — viewport-based, flips if no space above
  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const vh = window.innerHeight;
      const vw = window.innerWidth;

      // Prefer above; flip below if not enough space
      const spaceAbove = rect.top;
      const spaceBelow = vh - rect.bottom;
      const placement: 'top' | 'bottom' =
        spaceAbove >= POPOVER_MAX_HEIGHT + GAP || spaceAbove >= spaceBelow ? 'top' : 'bottom';

      const top =
        placement === 'top'
          ? Math.max(GAP, rect.top - POPOVER_MAX_HEIGHT - GAP)
          : Math.min(vh - POPOVER_MAX_HEIGHT - GAP, rect.bottom + GAP);

      // Align left with trigger, clamp to viewport
      const left = Math.max(GAP, Math.min(vw - POPOVER_WIDTH - GAP, rect.left));

      setPosition({ top, left, placement });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  const hasHistory = historyCount > 0;

  return (
    <>
      {/* Trigger — always visible, clickable only with history */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => hasHistory && setIsOpen(prev => !prev)}
        aria-expanded={isOpen}
        aria-haspopup={hasHistory ? 'true' : undefined}
        disabled={!hasHistory}
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] transition-colors',
          hasHistory
            ? 'cursor-pointer bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50'
            : 'cursor-default bg-muted/50 text-muted-foreground'
        )}
      >
        <Clock className="h-3 w-3" />
        <span>
          {hasHistory
            ? t('releaseHub.testing.previousTests', { count: historyCount })
            : t('releaseHub.testing.noPreviousTests')}
        </span>
      </button>

      {/* Popover content — rendered via portal to escape parent overflow/stacking contexts */}
      {isOpen &&
        position &&
        createPortal(
          <div
            ref={popoverRef}
            role="dialog"
            aria-label={t('releaseHub.testing.history')}
            style={{
              position: 'fixed',
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${POPOVER_WIDTH}px`,
              maxHeight: `${POPOVER_MAX_HEIGHT}px`,
              zIndex: 9999,
              backgroundColor: 'var(--card-bg)',
              color: 'var(--text-primary)',
              borderColor: 'var(--border-primary)',
            }}
            className={cn(
              'flex flex-col rounded-lg border shadow-2xl',
              'animate-in fade-in-0 zoom-in-95'
            )}
          >
            {/* Header */}
            <div className="border-b border-border px-3 py-2">
              <p className="text-xs font-semibold text-foreground">
                {t('releaseHub.testing.history')}
              </p>
            </div>

            {/* Body */}
            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
              {isLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}

              {!isLoading && isError && (
                <div className="flex flex-col items-center gap-2 py-3 text-xs">
                  <div className="inline-flex items-center gap-1.5 text-red-500">
                    <AlertTriangle className="h-4 w-4" />
                    <span>{t('releaseHub.testing.historyError')}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="rounded border border-border px-2 py-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    {t('releaseHub.testing.historyRetry')}
                  </button>
                </div>
              )}

              {!isLoading && !isError && (!history || history.length === 0) && (
                <p className="py-3 text-center text-xs text-muted-foreground">
                  {t('releaseHub.testing.historyEmpty')}
                </p>
              )}

              {!isLoading && !isError && history && history.length > 0 && (
                <ul className="space-y-3">
                  {history.map(item => (
                    <HistoryItem
                      key={item.id}
                      item={item}
                      locale={locale}
                      t={t}
                      totalSteps={totalSteps}
                    />
                  ))}
                </ul>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

// --- HistoryItem ---

interface HistoryItemProps {
  item: TestResultHistoryItem;
  locale: string;
  t: (key: string, vars?: TranslationParams) => string;
  totalSteps: number;
}

function HistoryItem({ item, locale, t, totalSteps }: HistoryItemProps) {
  const statusIcon =
    item.status === 'passed' ? (
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
    ) : (
      <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
    );

  const checkedCount = countCheckedSteps(item.stepResults);
  const duration = formatDuration(item.testedAt, item.archivedAt, t);
  const testedAtLocal = new Date(item.testedAt).toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <li className="rounded-md border border-border/60 p-2 hover:bg-accent/30">
      <div className="flex items-start gap-2">
        {statusIcon}
        <div className="min-w-0 flex-1 space-y-1">
          {/* Tester + tested date */}
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-xs font-semibold">{item.testerName}</span>
            <span className="shrink-0 text-[10px] text-muted-foreground">
              {t('releaseHub.testing.historyTestedAt')}: {testedAtLocal}
            </span>
          </div>

          {/* Meta row: step stats + active duration */}
          <div className="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
            {totalSteps > 0 && (
              <span className="inline-flex items-center gap-1 rounded bg-muted/40 px-1.5 py-0.5">
                <CheckCircle2 className="h-2.5 w-2.5" />
                {t('releaseHub.testing.historyStepStats', {
                  checked: checkedCount,
                  total: totalSteps,
                })}
              </span>
            )}
            {duration && (
              <span className="inline-flex items-center gap-1 rounded bg-muted/40 px-1.5 py-0.5">
                <Clock className="h-2.5 w-2.5" />
                {t('releaseHub.testing.historyActiveFor', { duration })}
              </span>
            )}
          </div>

          {/* Comment */}
          {item.comment && (
            <p className="text-[11px] italic text-muted-foreground">{item.comment}</p>
          )}

          {/* retestNote badge */}
          {item.retestNote && (
            <Badge className="mt-0.5 h-auto bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-500">
              {t('releaseHub.testing.retestReason')}: {item.retestNote}
            </Badge>
          )}
        </div>
      </div>
    </li>
  );
}
